import { useState, useEffect, RefObject } from 'react';

/**
 * ResizeObserverを使用してコンポーネントのサイズに応じたフォントサイズを計算するカスタムフック
 * @param containerRef - サイズを監視する要素のref
 * @param coefficient - フォントサイズ計算の係数。値が小さいほど文字が大きくなる
 * @returns 計算されたフォントサイズ(px)
 */
export const useResponsiveFontSize = (
    containerRef: RefObject<HTMLElement | null>,
    coefficient: number = 20,
): number => {
    const [fontSize, setFontSize] = useState(10); // 初期フォントサイズ

    useEffect(() => {
        let rafId = 0;
        let observer: ResizeObserver | null = null;

        const setup = () => {
            const container = containerRef.current;
            if (!container) {
                // 次のフレームで再試行（ref未セット対策）
                rafId = requestAnimationFrame(setup);
                return;
            }

            const rect = container.getBoundingClientRect();
            // サイズ0の間は初期サイズ計算を遅延
            if (rect.width === 0 || rect.height === 0) {
                rafId = requestAnimationFrame(setup);
                return;
            }

            // 初期サイズ反映
            const initialSize = Math.sqrt(rect.width * rect.height) / coefficient;
            setFontSize(Math.max(1, initialSize));

            // 監視開始（以後のリサイズは自動反映）
            observer = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    const { width, height } = entry.contentRect;
                    const newSize = Math.sqrt(width * height) / coefficient;
                    setFontSize(Math.max(1, newSize));
                }
            });
            observer.observe(container);
        };

        // 初期セットアップ
        rafId = requestAnimationFrame(setup);

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            if (observer) observer.disconnect();
        };
    }, [containerRef, coefficient]);

    return fontSize;
};
